import React, { useState } from 'react';
import { FaPaperPlane, FaBug, FaLightbulb, FaExclamationTriangle, FaStar, FaThumbsUp, FaThumbsDown, FaEnvelope, FaUser, FaTag } from 'react-icons/fa';

interface FeedbackForm {
  name: string;
  email: string;
  category: string;
  subject: string;
  message: string;
  priority: 'low' | 'medium' | 'high';
  pageUrl?: string;
  browserInfo?: string;
  attachments?: File[];
}

const Feedback: React.FC = () => {
  const [formData, setFormData] = useState<FeedbackForm>({
    name: '',
    email: '',
    category: '',
    subject: '',
    message: '',
    priority: 'medium',
    pageUrl: '',
    browserInfo: navigator.userAgent
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const feedbackCategories = [
    {
      id: 'bug',
      name: 'Bug Report',
      icon: FaBug,
      description: 'Report a technical issue or error',
      color: 'bg-red-500'
    },
    {
      id: 'feature',
      name: 'Feature Request',
      icon: FaLightbulb,
      description: 'Suggest a new feature or improvement',
      color: 'bg-blue-500'
    },
    {
      id: 'content',
      name: 'Content Issue',
      icon: FaExclamationTriangle,
      description: 'Report incorrect or missing content',
      color: 'bg-yellow-500'
    },
    {
      id: 'ui-ux',
      name: 'UI/UX Feedback',
      icon: FaStar,
      description: 'Feedback about design and user experience',
      color: 'bg-purple-500'
    },
    {
      id: 'performance',
      name: 'Performance Issue',
      icon: FaExclamationTriangle,
      description: 'Report slow loading or performance problems',
      color: 'bg-orange-500'
    },
    {
      id: 'general',
      name: 'General Feedback',
      icon: FaThumbsUp,
      description: 'General comments or suggestions',
      color: 'bg-green-500'
    }
  ];

  const priorityLevels = [
    { value: 'low', label: 'Low', color: 'bg-green-100 text-green-800' },
    { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'high', label: 'High', color: 'bg-red-100 text-red-800' }
  ];

  const pageCategories = [
    { value: '', label: 'Select a page (optional)' },
    { value: '/', label: 'Home Page' },
    { value: '/alphabet-learning', label: 'Alphabet Learning' },
    { value: '/vocabulary-garden', label: 'Vocabulary Garden' },
    { value: '/vocabulary', label: 'Vocabulary' },
    { value: '/vocabulary/food', label: 'Food Vocabulary' },
    { value: '/vocabulary/weather', label: 'Weather Vocabulary' },
    { value: '/cultural/tehran', label: 'Tehran Cultural Page' },
    { value: '/hafez', label: 'Hafez Lessons' },
    { value: '/hafez/flashcards', label: 'Hafez Flashcards' },
    { value: '/forum', label: 'Forum' },
    { value: '/about', label: 'About Page' },
    { value: '/pricing', label: 'Pricing Page' },
    { value: '/login', label: 'Login Page' },
    { value: '/register', label: 'Register Page' },
    { value: '/dashboard', label: 'Dashboard' },
    { value: '/profile', label: 'Profile Page' },
    { value: '/progress', label: 'Progress Page' },
    { value: '/quiz', label: 'Quiz Page' },
    { value: '/flashcards', label: 'Flashcards Page' },
    { value: '/pathways', label: 'Learning Pathways' },
    { value: '/blog', label: 'Blog' },
    { value: '/contact', label: 'Contact Page' },
    { value: 'other', label: 'Other Page' }
  ];

  const handleInputChange = (field: keyof FeedbackForm, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Here you would typically send the data to your backend
      console.log('Feedback submitted:', formData);
      
      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        category: '',
        subject: '',
        message: '',
        priority: 'medium',
        pageUrl: '',
        browserInfo: navigator.userAgent
      });
    } catch (error) {
      setSubmitError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedCategory = feedbackCategories.find(cat => cat.id === formData.category);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <div className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              📝 Feedback & Support
            </h1>
            <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto px-4">
              Help us improve Vajak by sharing your thoughts, reporting issues, or suggesting new features. 
              Your feedback is invaluable to us!
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {submitSuccess ? (
          <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 text-center">
            <div className="text-green-500 text-4xl sm:text-6xl mb-4">✅</div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Thank You!</h2>
            <p className="text-sm sm:text-base text-gray-600 mb-6">
              Your feedback has been submitted successfully. We'll review it and get back to you soon.
            </p>
            <button
              onClick={() => setSubmitSuccess(false)}
              className="bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm sm:text-base"
            >
              Submit Another Feedback
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
            {/* Category Selection */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 lg:sticky lg:top-8">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <FaTag className="mr-2 text-sm sm:text-base" />
                  Feedback Category
                </h2>
                <div className="space-y-2 sm:space-y-3">
                  {feedbackCategories.map(category => (
                    <button
                      key={category.id}
                      onClick={() => handleInputChange('category', category.id)}
                      className={`w-full p-3 sm:p-4 rounded-lg border-2 transition-all text-left touch-manipulation ${
                        formData.category === category.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center mb-1 sm:mb-2">
                        <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full ${category.color} flex items-center justify-center text-white mr-2 sm:mr-3`}>
                          <category.icon className="text-xs sm:text-sm" />
                        </div>
                        <span className="font-semibold text-gray-900 text-sm sm:text-base">{category.name}</span>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600">{category.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Feedback Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 lg:p-8">
                {selectedCategory && (
                  <div className={`inline-flex items-center px-3 sm:px-4 py-1 sm:py-2 rounded-full text-white text-xs sm:text-sm font-medium mb-4 sm:mb-6 ${selectedCategory.color}`}>
                    <selectedCategory.icon className="mr-1 sm:mr-2" />
                    {selectedCategory.name}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  {/* Personal Information */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <FaUser className="inline mr-2" />
                        Name (Optional)
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <FaEnvelope className="inline mr-2" />
                        Email (Optional)
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => handleInputChange('subject', e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      placeholder="Brief description of your feedback"
                      required
                    />
                  </div>

                  {/* Page Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Related Page (Optional)
                    </label>
                    <select
                      value={formData.pageUrl}
                      onChange={(e) => handleInputChange('pageUrl', e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                    >
                      {pageCategories.map(page => (
                        <option key={page.value} value={page.value}>
                          {page.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priority Level
                    </label>
                    <div className="flex flex-wrap gap-2 sm:gap-4">
                      {priorityLevels.map(priority => (
                        <label key={priority.value} className="flex items-center">
                          <input
                            type="radio"
                            name="priority"
                            value={priority.value}
                            checked={formData.priority === priority.value}
                            onChange={(e) => handleInputChange('priority', e.target.value)}
                            className="mr-2"
                          />
                          <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${priority.color}`}>
                            {priority.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Detailed Message *
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      rows={4}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base resize-y"
                      placeholder="Please provide detailed information about your feedback, issue, or suggestion..."
                      required
                    />
                  </div>

                  {/* Error Message */}
                  {submitError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
                      <p className="text-red-600 text-sm sm:text-base">{submitError}</p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={isSubmitting || !formData.category || !formData.subject || !formData.message}
                      className={`flex items-center px-4 sm:px-6 lg:px-8 py-2 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base touch-manipulation ${
                        isSubmitting || !formData.category || !formData.subject || !formData.message
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white mr-2"></div>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <FaPaperPlane className="mr-2" />
                          Submit Feedback
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Additional Information */}
        <div className="mt-6 sm:mt-8 bg-white rounded-xl shadow-lg p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4">💡 Tips for Better Feedback</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">For Bug Reports:</h4>
              <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                <li className="text-gray-700">• Describe what you were trying to do</li>
                <li className="text-gray-700">• Include steps to reproduce the issue</li>
                <li className="text-gray-700">• Mention your browser and device</li>
                <li className="text-gray-700">• Add screenshots if possible</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">For Feature Requests:</h4>
              <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                <li className="text-gray-700">• Explain the problem you're solving</li>
                <li className="text-gray-700">• Describe your ideal solution</li>
                <li className="text-gray-700">• Provide use case examples</li>
                <li className="text-gray-700">• Consider existing alternatives</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Feedback; 